import { FC, ReactNode } from 'react';

import '@fontsource/roboto-mono';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import { css, styled } from '@mui/material/styles';

import { useToggle } from 'hooks/useToggle';

import Icon from 'components/Icons/Icon';
import { Text } from 'components/UIkit/atoms/Text';

interface Props {
  contentText: string;
  headerText: string;
  icon: ReactNode;
}

export const AlertCard: FC<Props> = ({ headerText, contentText, icon }) => {
  const { isOpen, toggle } = useToggle(false);

  return (
    <StyledContainer display="flex" flexDirection="column" gap={8}>
      <Accordion disableGutters expanded={isOpen} onClick={toggle}>
        <AccordionSummary>
          <StyledHeader isExpanded={isOpen}>
            {icon}
            <Text variant="subtext" flexGrow={1}>
              {headerText}
            </Text>
            <StyledChevron className={isOpen ? 'up' : 'down'} />
          </StyledHeader>
        </AccordionSummary>

        <AccordionDetails>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="start"
            width="100%"
            gap={8}
            pt={8}
            px={16}
            pb={16}
          >
            <StyledTypography isExpanded={isOpen}>{contentText}</StyledTypography>
          </Box>
        </AccordionDetails>
      </Accordion>
    </StyledContainer>
  );
};

export const StyledHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isExpanded'
})<{
  isExpanded?: boolean;
}>(
  ({ theme, isExpanded }) => css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: start;
    gap: 8px;
    width: 100%;
    padding: 8px 16px;
    border-radius: ${isExpanded
      ? `${theme.borderRadius.large} ${theme.borderRadius.large} 0 0`
      : theme.borderRadius.large};
    background: rgba(0, 0, 0, 0);
    transition: border-radius 0.2s ease-in-out, background 0.3s ease-in-out;

    &:hover {
      background: rgba(0, 0, 0, 0.06);
    }

    > * {
      z-index: 1;
    }

    &:hover .icon-with-rotation {
      color: ${theme.palette.secondary.main};
    }
  `
);
const StyledContainer = styled(Box)(
  ({ theme }) => css`
    background: ${theme.backgrounds.canopyGradient};
    border-radius: ${theme.borderRadius.large};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  `
);

const StyledTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isExpanded'
})<{
  isExpanded?: boolean;
}>(
  ({ isExpanded }) => css`
    font-family: Roboto Mono, monospace;
    font-weight: 400;
    font-size: 12px;
    line-height: 14px;
    letter-spacing: 0;
    opacity: ${isExpanded ? 1 : 0};
    white-space: pre-wrap;
    transition: opacity 0.2s ease-in-out;
  `
);

const StyledChevron = styled(Icon.Chevron)(
  ({ theme }) => css`
    color: ${theme.palette.text.disabled};
    transition: color 0.3s ease-in-out;
  `
);
